// pricing-occupancy — pure helpers for the "Belegung & Kinder" card
// (Booking step 2). Rows are the SAME objects the fees form's Alpine `fees`
// array holds (pricingFeeRowVM JSON shape). The example sentence mirrors
// the engine's rules (internal/pricing/occupancy.go): children in an active
// child group never count as extra guests; each extra guest pays the tier
// with the greatest start <= its surplus number (blank = the first extra
// guest). Never throws.
import { fill, formatEuros } from "./pricing-summary";

export interface OccRow {
  kind?: string;
  amount_type?: string;
  amount_euros?: string;
  scope?: string;
  from_extra_guest?: string | number;
  age_min?: string | number;
  age_max?: string | number;
  guest_max?: string | number;
  part_of_accommodation_base?: boolean | string;
  disabled?: boolean;
  provenance?: string;
  [k: string]: unknown;
}
export type OccKind = "single" | "tier" | "child" | "";
export interface OccPhrases {
  prefix: string;
  adults_one: string;
  adults_other: string;
  child_one: string;
  child_other: string;
  plus: string;
  arrow: string;
  part: string;
  single_part: string;
  per_night: string;
  none: string;
}
export interface OccCtx {
  currency: string;
  includedGuests: number;
  guestCountAge: number;
  maxOccupancy: number | null;
  phrases: OccPhrases;
}

const truthy = (v: unknown) => v === true || v === "true";
const num = (v: unknown): number | null => {
  const s = String(v ?? "").trim();
  if (s === "") return null;
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};
const active = (r: OccRow | undefined) => !!r && !r.disabled;
// Standard occupancy comes from an x-model.number input: clearing it yields
// "" (not 0). Clamp at every entry point so "" + 1 never becomes "1"-style
// string concatenation.
const ig = (v: unknown): number => Math.max(0, Number(v) || 0);

export function isOccupancyFee(r: OccRow | null | undefined): boolean {
  return !!r && truthy(r.part_of_accommodation_base);
}

export function rowKind(r: OccRow | null | undefined): OccKind {
  if (!r || !isOccupancyFee(r)) return "";
  if (num(r.guest_max) === 1) return "single";
  if (r.kind === "extra_guest") return "tier";
  if (r.kind === "child_band") return "child";
  return "";
}

export function isEditableInline(r: OccRow | null | undefined): boolean {
  if (!r || r.amount_type !== "flat") return false;
  return rowKind(r) === "single" ? r.scope === "per_night" : r.scope === "per_guest_per_night";
}

function indexesOf(rows: OccRow[], kind: OccKind): number[] {
  const out: number[] = [];
  (rows || []).forEach((r, i) => { if (rowKind(r) === kind) out.push(i); });
  return out;
}

export function isFirstTier(rows: OccRow[], idx: number): boolean {
  const t = indexesOf(rows, "tier");
  return t.length > 0 && t[0] === idx;
}

/** A tier's start as a 1-based surplus-guest number; blank/invalid => 1. */
export function effectiveFromExtra(r: OccRow): number {
  const n = num(r?.from_extra_guest);
  return n === null || n < 1 ? 1 : Math.floor(n);
}

export function tierErrors(rows: OccRow[], includedGuests: number, maxOccupancy: number | null): Record<number, "first" | "order" | "never"> {
  const inc = ig(includedGuests);
  const out: Record<number, "first" | "order" | "never"> = {};
  let prev: number | null = null;
  for (const i of indexesOf(rows, "tier")) {
    if (!active(rows[i])) continue;
    const from = effectiveFromExtra(rows[i]);
    // "never" is evaluated for the FIRST tier as well (spec §2 compares
    // included + fromExtra with max. occupancy unconditionally), so it cannot
    // live in an else-branch hanging off `prev === null`.
    if (prev === null && from !== 1) {
      out[i] = "first";
    } else if (prev !== null && from <= prev) {
      out[i] = "order";
    } else if (maxOccupancy !== null && maxOccupancy > 0 && inc + from > maxOccupancy) {
      out[i] = "never";
    }
    prev = prev === null ? from : Math.max(prev, from);
  }
  return out;
}

export function childGroupOverlaps(rows: OccRow[]): number[] {
  const groups = indexesOf(rows, "child").filter((i) => active(rows[i]))
    .map((i) => ({ i, min: num(rows[i].age_min) ?? 0, max: num(rows[i].age_max) ?? 17 }));
  const bad = new Set<number>();
  for (let a = 0; a < groups.length; a++) {
    for (let b = a + 1; b < groups.length; b++) {
      if (groups[a].min <= groups[b].max && groups[b].min <= groups[a].max) {
        bad.add(groups[a].i);
        bad.add(groups[b].i);
      }
    }
  }
  return [...bad].sort((x, y) => x - y);
}

export function normalizeTiers(rows: OccRow[]): void {
  const t = indexesOf(rows, "tier");
  if (t.length > 0) rows[t[0]].from_extra_guest = "1";
}

const BASE_ROW: OccRow = {
  id: "", name: "", kind: "custom", inclusion: "mandatory", amount_type: "flat", amount_euros: "0.00",
  percent: "0", percent_base: "accommodation", scope: "per_night", service_cat: "lodging",
  night_min: "", night_max: "", guest_min: "", guest_max: "", age_min: "", age_max: "",
  date_from: "", date_to: "", part_of_accommodation_base: true, max_quantity: "", from_extra_guest: "",
  provenance: "added", disabled: false,
};

export function newTier(rows: OccRow[]): OccRow {
  const t = indexesOf(rows, "tier");
  const from = t.length === 0 ? "1" : String(Math.max(...t.map((i) => effectiveFromExtra(rows[i]))) + 1);
  return { ...BASE_ROW, kind: "extra_guest", scope: "per_guest_per_night", from_extra_guest: from };
}

export function newChildGroup(rows: OccRow[]): OccRow {
  const maxes = indexesOf(rows, "child").map((i) => num(rows[i].age_max) ?? -1);
  const start = maxes.length ? Math.max(...maxes) + 1 : 0;
  return { ...BASE_ROW, kind: "child_band", scope: "per_guest_per_night", age_min: String(Math.min(start, 17)), age_max: "17" };
}

export function newSingle(): OccRow {
  return { ...BASE_ROW, kind: "custom", scope: "per_night", guest_max: "1" };
}

export function splitSigned(s: unknown): { sign: "plus" | "minus"; abs: string } {
  const t = String(s ?? "").trim();
  return t.startsWith("-") ? { sign: "minus", abs: t.slice(1) } : { sign: "plus", abs: t.replace(/^\+/, "") };
}

export function joinSigned(sign: string, abs: unknown): string {
  const a = String(abs ?? "").trim().replace(/^[-+]/, "");
  if (a === "") return "";
  return sign === "minus" ? "-" + a : a;
}

export function turnOff(rows: OccRow[], maxOccupancy: number | null): { fees: OccRow[]; includedGuests: number | null } {
  const fees: OccRow[] = [];
  for (const r of rows || []) {
    // Only the card's own rows (rowKind !== "") are touched; a legacy
    // part-of-base row lives in the fee list and must survive "Nein".
    if (rowKind(r) === "") fees.push(r);
    else if (r.provenance === "inherited" || r.provenance === "overridden") fees.push({ ...r, disabled: true });
  }
  return { fees, includedGuests: maxOccupancy !== null && maxOccupancy > 0 ? maxOccupancy : null };
}

export function hasActiveOccupancy(rows: OccRow[]): boolean {
  return (rows || []).some((r) => isOccupancyFee(r) && active(r) && rowKind(r) !== "");
}

export function occupancySample(rows: OccRow[], ctx: OccCtx): OccSample {
  ctx = { ...ctx, includedGuests: ig(ctx.includedGuests) };
  const tiers = indexesOf(rows, "tier").filter((i) => active(rows[i]));
  const kids = indexesOf(rows, "child").filter((i) => active(rows[i]))
    .sort((a, b) => (num(rows[a].age_min) ?? 0) - (num(rows[b].age_min) ?? 0));
  const childAges = kids.length ? [Math.floor(((num(rows[kids[0]].age_min) ?? 0) + (num(rows[kids[0]].age_max) ?? 17)) / 2)] : [];
  const hasSingle = indexesOf(rows, "single").some((i) => active(rows[i]));
  let adults = tiers.length ? ctx.includedGuests + 2 : Math.max(ctx.includedGuests, 1);
  if (!tiers.length && !kids.length && hasSingle) adults = 1;
  if (ctx.maxOccupancy !== null && ctx.maxOccupancy > 0) {
    const cap = ctx.maxOccupancy - childAges.filter((a) => a >= ctx.guestCountAge).length;
    adults = Math.max(1, Math.min(adults, cap));
  }
  return { adults, childAges };
}

/** One example party: adults of unknown age plus each child's age. */
export interface OccSample { adults: number; childAges: number[] }

/** How many guests each row charges, keyed by row index. */
export interface OccAllocation {
  /** tier row index -> guests charged at that tier */
  tier: Map<number, number>;
  /** child-group row index -> children charged at that group */
  child: Map<number, number>;
}

/**
 * occupancyAllocate mirrors allocateOccupancy in internal/pricing/occupancy.go
 * exactly — it is what makes the card's example sentence agree with the price
 * the engine will actually quote.
 *
 * A guest counts only if its age is unknown (an adult) or >= guestCountAge; an
 * infant below that age is excluded from the ordering entirely, so it is never
 * numbered and never pays, even when a child group's age window covers it
 * (countingGuestOrder). The counting guests are ordered adults first, then
 * children oldest first, and the first includedGuests of them fill the
 * included places. Every surplus guest then pays either its matching child
 * group — the FIRST active group in row order wins (childBandFeeForGuest), and
 * it consumes no extra-guest number — or, numbered 1..n, the tier with the
 * greatest start <= its number, with no matching tier meaning no charge
 * (tierForExtraGuest).
 *
 * The engine's max(IncludedGuests, 0) clamp lives in two places — the
 * included-places cut and EffectiveFromExtraGuest's legacy conversion. Here
 * ig() is that clamp for the cut; the conversion has already happened server
 * side (feeRowVMFromLine calls EffectiveFromExtraGuest), so a row reaching this
 * code always carries a relative from_extra_guest and never a legacy absolute
 * start.
 *
 * Two things the mirror deliberately does NOT reproduce, neither reachable
 * from the card today:
 *  - FeeConditions.Matches. The engine gates every tier and child group on
 *    NightMin/NightMax/GuestMin/GuestMax/DateRange; an example sentence has no
 *    stay to evaluate them against (no arrival date, no night count), so every
 *    active row is treated as matching. A row whose gates the real stay fails
 *    would show in the example and not in the quote.
 *  - An unbounded age window. Go reads a nil AgeMax as "no upper bound";
 *    num(age_max) ?? 17 caps a blank one at 17 here, because the card's own
 *    editor always writes both ends (newChildGroup defaults age_max to "17").
 * Never throws.
 */
export function occupancyAllocate(rows: OccRow[], ctx: OccCtx, sample: OccSample): OccAllocation {
  const inc = ig(ctx.includedGuests);
  const tiers = indexesOf(rows, "tier").filter((i) => active(rows[i]));
  const kids = indexesOf(rows, "child").filter((i) => active(rows[i]));
  const out: OccAllocation = { tier: new Map(), child: new Map() };

  // Adults are the nil-age guests and sort first; counting children follow,
  // oldest first. Infants are dropped here, before any numbering.
  const order: (number | null)[] = [];
  for (let i = 0; i < Math.max(0, Math.floor(sample.adults) || 0); i++) order.push(null);
  (sample.childAges || [])
    .filter((a) => a >= ctx.guestCountAge)
    .sort((a, b) => b - a)
    .forEach((a) => order.push(a));

  let surplusNo = 0;
  for (const age of order.slice(inc)) {
    if (age !== null) {
      const g = kids.find((i) => age >= (num(rows[i].age_min) ?? 0) && age <= (num(rows[i].age_max) ?? 17));
      if (g !== undefined) {
        out.child.set(g, (out.child.get(g) || 0) + 1);
        continue;
      }
    }
    surplusNo++;
    let best = -1;
    for (const i of tiers) {
      const from = effectiveFromExtra(rows[i]);
      if (from > surplusNo) continue;
      if (best === -1 || from > effectiveFromExtra(rows[best])) best = i;
    }
    if (best >= 0) out.tier.set(best, (out.tier.get(best) || 0) + 1);
  }
  return out;
}

export function occupancyExample(rows: OccRow[], ctx: OccCtx, sample?: OccSample): string {
  if (!hasActiveOccupancy(rows)) return "";
  ctx = { ...ctx, includedGuests: ig(ctx.includedGuests) };
  const ph = ctx.phrases || ({} as OccPhrases);
  const { adults, childAges } = sample ?? occupancySample(rows, ctx);
  const alloc = occupancyAllocate(rows, ctx, { adults, childAges });
  const parts: string[] = [];

  for (const i of indexesOf(rows, "tier")) {
    const n = alloc.tier.get(i);
    if (n) parts.push(fill(ph.part, { n, amount: formatEuros(rows[i].amount_euros, ctx.currency) }));
  }
  for (const i of indexesOf(rows, "child")) {
    const n = alloc.child.get(i);
    if (n) parts.push(fill(ph.part, { n, amount: formatEuros(rows[i].amount_euros, ctx.currency) }));
  }

  // Tier/child parts are unsigned ("2 × 25,00 €"); the sum gets ONE leading
  // "+". The single-occupancy part carries its own sign and only ever
  // appears alone (adults === 1 and no children ⇒ no tier/child parts).
  let rhsParts = parts.length ? "+" + parts.join(ph.plus) : "";
  const singleIdx = indexesOf(rows, "single").find((i) => active(rows[i]));
  if (singleIdx !== undefined && adults === 1 && childAges.length === 0) {
    const s = splitSigned(rows[singleIdx].amount_euros);
    rhsParts = fill(ph.single_part, { sign: s.sign === "minus" ? "−" : "+", amount: formatEuros(s.abs, ctx.currency) });
  }

  const who = [fill(adults === 1 ? ph.adults_one : ph.adults_other, { n: adults })];
  if (childAges.length) who.push(fill(childAges.length === 1 ? ph.child_one : ph.child_other, { n: childAges.length, ages: childAges.join(", ") }));
  const rhs = rhsParts ? rhsParts + " " + ph.per_night : ph.none;
  return `${ph.prefix} ${who.join(ph.plus)}${ph.arrow}${rhs}`;
}

export const pricingOccupancy = {
  isOccupancyFee, rowKind, isEditableInline, isFirstTier, effectiveFromExtra, tierErrors, childGroupOverlaps,
  normalizeTiers, newTier, newChildGroup, newSingle, splitSigned, joinSigned, turnOff, hasActiveOccupancy,
};
